// LiveLoveSpain CRM - Standalone Frontend
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from './contexts/LanguageContext';
import { CRMProvider } from './contexts/CRMContext';
import { FeatureRegistryProvider } from './contexts/FeatureRegistryContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { CommandPaletteProvider } from './contexts/CommandPaletteContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TourProvider } from './contexts/TourContext';
import { DialogProvider } from './components/kibo-ui/dialog';
import LanguageIconSprite from './components/ui/LanguageIconSprite';
import CRMLayout from './components/crm/CRMLayout';
import Login from './components/crm/Login';
import Signup from './components/crm/Signup';
import VerifyEmail from './components/crm/VerifyEmail';
import ResetPassword from './components/crm/ResetPassword';
import LandingPage from './components/crm/LandingPage';
import ContactSales from './components/crm/ContactSales';
import Terms from './components/crm/Terms';
import Cookies from './components/crm/Cookies';
import DPA from './components/crm/DPA';
import Dashboard from './components/crm/Dashboard';
import AdminDashboard from './components/admin/AdminDashboard';
import Clients from './components/crm/Clients';
import ClientDetail from './components/crm/ClientDetailEnhanced';
import ClientForm from './components/crm/ClientForm';
import Leads from './components/crm/Leads';
import LeadForm from './components/crm/LeadForm';
import LeadDetail from './components/crm/LeadDetail';
import Tasks from './components/crm/Tasks';
import TaskForm from './components/crm/TaskForm';
import TaskDetail from './components/crm/TaskDetail';
import TaskTemplates from './components/crm/TaskTemplates';
import TaskTemplateForm from './components/crm/TaskTemplateForm';
import TaskTemplateDetail from './components/crm/TaskTemplateDetail';
import AgencySettings from './components/crm/AgencySettings';
import Settings from './components/crm/Settings';
import Analytics from './components/crm/Analytics';
// Additional imports for missing routes
import Accounts from './components/crm/Accounts';
import AccountDetailView from './components/crm/AccountDetailView';

// Wrapper component to adapt AccountDetailView for routing
const AccountDetailViewWrapper = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const handleBack = () => navigate('/accounts');
    const handleEdit = (account) => navigate(`/accounts/${id}/edit`);
    const handleDelete = async (accountId) => {
        // Implement delete logic here
        //console.log('Delete account:', accountId);
    };
    
    return (
        <AccountDetailView 
            accountId={id}
            onBack={handleBack}
            onEdit={handleEdit}
            onDelete={handleDelete}
        />
    );
};
import AccountForm from './components/crm/AccountForm';
import OpportunityForm from './components/crm/OpportunityForm';
import AICopilot from './components/crm/AICopilot';
import EngagementHealth from './components/crm/EngagementHealth';
import CollaborationHub from './components/crm/CollaborationHub';
import CampaignsEnhanced from './components/crm/CampaignsEnhanced';
import CampaignAnalytics from './components/crm/CampaignAnalytics';

// Redirect component for old email-campaigns routes
const EmailCampaignRedirect = () => {
    const { id } = useParams();
    const location = useLocation();
    if (location.pathname.includes('/edit')) {
        return <Navigate to={`/campaigns/${id}/edit`} replace />;
    } else if (location.pathname.includes('/analytics')) {
        return <Navigate to={`/campaigns/${id}/analytics`} replace />;
    } else {
        return <Navigate to={`/campaigns/${id}`} replace />;
    }
};
import CampaignBuilder from './components/crm/CampaignBuilder';
import AudienceBuilder from './components/crm/AudienceBuilder';
import SendingProfiles from './components/crm/SendingProfiles';
import SendingProfileBuilder from './components/crm/SendingProfileBuilder';
import EmailTemplatesEnhanced from './components/crm/EmailTemplatesEnhanced';
import EmailTemplateBuilder from './components/crm/EmailTemplateBuilder';
import Calendar from './components/crm/Calendar';
import Workflows from './components/crm/Workflows';
import CreateWorkflow from './components/crm/CreateWorkflow';
import VisualWorkflowBuilder from './components/crm/VisualWorkflowBuilder';
import WorkflowDetail from './components/crm/WorkflowDetail';
import WorkflowEdit from './components/crm/WorkflowEdit';
import TaskAutomation from './components/crm/TaskAutomation';
import Documents from './components/crm/Documents';
import Locations from './components/crm/Locations';
import Insights from './components/crm/Insights';
import Billing from './components/crm/Billing';
import AgencyBilling from './components/crm/AgencyBilling';
import SubscriptionPage from './components/crm/SubscriptionPage';
import SubscriptionSuccessPage from './components/crm/SubscriptionSuccessPage';
import TrialStatusBanner from './components/crm/TrialStatusBanner';
import ClientPortal from './components/crm/ClientPortal';
import AcceptInvite from './components/crm/AcceptInvite';
import MultiLanguage from './components/crm/MultiLanguage';
import ConsultantMode from './components/crm/ConsultantMode';
import RelocationJourney from './components/crm/RelocationJourney';
import RelocationWorkflowTemplates from './components/crm/RelocationWorkflowTemplates';
import RelocationWorkspace from './components/crm/RelocationWorkspace';
import RelocationWorkspaceSelector from './components/crm/RelocationWorkspaceSelector';
import NotFound from './components/NotFound';
import TourManager from './components/TourManager';
import CommandPalette from './components/ui/command-palette';
import NotificationCenter from './components/ui/notification-center';
import { useCRM } from './contexts/CRMContext';
import { PERMISSIONS, hasPermission } from './utils/permissions';
import FormsHome from './components/forms/FormsHome';
import FormBuilder from './components/forms/FormBuilder';
import FormResponses from './components/forms/FormResponses';
import FormRender from './components/forms/FormRender';
import FormResponseDetail from './components/forms/FormResponseDetail';
import FormAnalytics from './components/forms/FormAnalytics';
import TemplatesGallery from './components/forms/TemplatesGallery';

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-300">Loading CRM...</p>
    </div>
  </div>
);

// Root redirect component - show landing page if no token, otherwise redirect to dashboard
const RootRedirect = () => {
  const token = localStorage.getItem('crm_token');
  const params = useParams();
  const lang = params.lang;
  
  // If language parameter exists and is valid, update language context
  React.useEffect(() => {
    if (lang && ['en', 'es', 'fr', 'de', 'pt'].includes(lang)) {
      // Language will be detected by LanguageContext from URL path
      // No need to manually set it here
    }
  }, [lang]);
  
  if (token) {
    // If on language-prefixed route, redirect to non-prefixed dashboard
    if (lang) {
      return <Navigate to="/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  } else {
    return <LandingPage />;
  }
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('crm_token');
  const hostname = window.location.hostname;
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  
  // Debug: Log token status
  //console.log('ProtectedRoute - Token check:', token ? 'Token exists' : 'No token');
  
  if (!token) {
    //console.log('ProtectedRoute - Redirecting to login');
    // Simple path - basename handles /crm prefix automatically
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Permission route wrapper - must be used inside CRMProvider
const PermissionRoute = ({ permission, children }) => {
  const { user, currentAgency, loading } = useCRM();
  
  // Wait for user to load
  if (loading) {
    return <LoadingFallback />;
  }
  
  // Check permission
  const currentAgencyId = currentAgency?._id || currentAgency?.id;
  
  // Helper to get user role for logging
  const getUserRoleForLog = () => {
    if (!user?.agencyMemberships || !Array.isArray(user.agencyMemberships)) return 'none';
    if (currentAgencyId) {
      const membership = user.agencyMemberships.find(
        m => {
          const mAgencyId = m.agencyId?._id || m.agencyId;
          return String(mAgencyId) === String(currentAgencyId);
        }
      );
      return membership?.role || 'none';
    }
    // If no currentAgencyId, find first admin or first membership
    const membership = user.agencyMemberships.find(m => m.role === 'agency_admin') || user.agencyMemberships[0];
    return membership?.role || 'none';
  };
  
  if (!user || !hasPermission(user, permission, currentAgencyId)) {
    const hostname = window.location.hostname;
    const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
    //console.warn('PermissionRoute: Access denied for permission:', permission, 'User role:', getUserRoleForLog(), 'Agency:', currentAgencyId);
    // Simple path - basename handles /crm prefix automatically
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public route wrapper (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const token = localStorage.getItem('crm_token');
  const hostname = window.location.hostname;
  const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
  
  if (token) {
    // Simple path - basename handles /crm prefix automatically
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Ignore ResizeObserver errors - they're harmless and common with libraries like ReactFlow
    const errorMessage = error?.message || error?.toString() || '';
    if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Don't set error state for ResizeObserver errors
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Ignore ResizeObserver errors - they're harmless and common with libraries like ReactFlow
    const errorMessage = error?.message || error?.toString() || '';
    if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications')) {
      // Suppress ResizeObserver errors - they're harmless
      return;
    }
    //console.error('App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center p-8">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper component to preserve language prefix in navigation
const LanguageAwareNavigate = ({ to }) => {
  const params = useParams();
  const lang = params.lang;
  const navigate = useNavigate();
  const supportedLangs = ['en', 'es', 'fr', 'de', 'pt'];
  
  React.useEffect(() => {
    if (lang && supportedLangs.includes(lang)) {
      navigate(`/${lang}${to}`, { replace: true });
    } else {
      navigate(to, { replace: true });
    }
  }, [lang, to, navigate]);
  
  return null;
};

// Route switch for protected CRM routes
const CRMSwitch = () => {
  const location = useLocation();
  let pathname = location.pathname;
  
  // Remove /crm prefix if present (for main domain routing)
  // React Router's basename handles route matching, but location.pathname still includes the full path
  if (pathname.startsWith('/crm/')) {
    pathname = pathname.replace(/^\/crm/, '');
  } else if (pathname === '/crm') {
    pathname = '/';
  }
  
  // Handle root path - show landing page or redirect to dashboard
  if (pathname === '/' || pathname === '') {
    return <RootRedirect />;
  }
  
  // Don't handle public routes - they're handled by specific routes above
  const publicRoutePaths = ['/login', '/signup', '/verify-email', '/reset-password', '/contact', '/terms', '/cookies', '/dpa', '/portal/accept-invite', '/public/forms'];
  if (publicRoutePaths.some(path => pathname.startsWith(path))) {
    return null; // Let the specific route handle it
  }
  
  // List of valid CRM route patterns (base paths only, dynamic segments handled by nested routes)
  const validRoutePatterns = [
    '/dashboard', '/admin', '/test',
    '/leads', '/clients', '/tasks', '/task-templates',
    '/settings', '/agency-settings', '/analytics',
    '/accounts', '/opportunities',
    '/ai-copilot', '/engagement-health', '/collaboration',
    '/email-campaigns', '/campaigns', '/audiences',
    '/sending-profiles', '/email-templates',
    '/calendar', '/workflows', '/task-automation',
    '/documents', '/locations', '/insights',
    '/billing', '/subscription', '/agency',
    '/client-portal', '/multi-language', '/forms',
    '/consultant-mode', '/relocation-journey', '/relocation-workflows', '/relocation-workspace'
  ];
  
  // Check if pathname matches any valid route pattern (exact match or starts with pattern)
  const isValidRoute = validRoutePatterns.some(pattern => {
    // Exact match
    if (pathname === pattern) return true;
    // Starts with pattern followed by / or end of string (for nested routes like /clients/123)
    if (pathname.startsWith(pattern + '/')) return true;
    // Special case for root-level dynamic routes
    if (pattern.includes(':') && pathname.match(new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '(/|$)'))) return true;
    return false;
  });
  
  // If route is invalid, show 404 regardless of authentication status
  if (!isValidRoute) {
    return <NotFound />;
  }
  
  // All valid routes are protected CRM routes
  return (
    <ProtectedRoute>
      <CRMLayout />
    </ProtectedRoute>
  );
};

function App() {
  // Clear any existing tokens on app start for debugging
  React.useEffect(() => {
    //console.log('App starting - Current token:', localStorage.getItem('crm_token'));
    // Uncomment the next line to clear tokens for testing
    // localStorage.removeItem('crm_token');
    
    // Clean up URL parameters only if coming from livelovespain.com
    const hostname = window.location.hostname;
    const isSubdomain = hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.');
    const referrer = document.referrer;
    
    // Check if referrer is from livelovespain.com (main domain)
    const isFromMainDomain = referrer && (
      referrer.includes('livelovespain.com') && 
      !referrer.includes('crm.livelovespain.com')
    );
    
    if (isSubdomain && isFromMainDomain && window.location.search) {
      // Remove all URL parameters by replacing the URL without the query string
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  // Determine basename based on domain
  // If on crm.livelovespain.com subdomain or localhost, use root path
  // Otherwise, use /crm as base path (for main domain)
  const getBasename = () => {
    const hostname = window.location.hostname;
    // If on the CRM subdomain or localhost (for development), use root path
    if (hostname === 'crm.livelovespain.com' || hostname.startsWith('crm.') || hostname === 'localhost' || hostname === '127.0.0.1') {
      return '';
    }
    // Otherwise, use /crm as base path (for main domain)
    return '/crm';
  };

  const basename = getBasename();

  return (
    <ErrorBoundary>
      <LanguageIconSprite />
      <HelmetProvider>
        <Router
          basename={basename}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <LanguageProvider>
          <CRMProvider>
          <FeatureRegistryProvider>
          <BrandingProvider>
          <SystemConfigProvider>
            <CommandPaletteProvider>
              <NotificationProvider>
                <TourProvider>
                  <DialogProvider>
                    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                      {/* Toast Notifications */}
                      <Toaster 
                        position="bottom-right"
                        toastOptions={{
                          duration: 5000,
                          style: {
                            background: '#fff',
                            color: '#333',
                            borderRadius: '8px',
                            padding: '12px 16px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            minWidth: '300px',
                          },
                        }}
                        containerStyle={{
                          bottom: '20px',
                          right: '20px',
                        }}
                      />
                      
                      {/* Command Palette */}
                      <CommandPalette />
                      
                      {/* Notification Center */}
                      <NotificationCenter />
                      
                      {/* Tour Manager */}
                      <TourManager />
                      
                      {/* Main Application Routes */}
                      <Routes>
                        {/* Language-prefixed public routes (e.g., /fr/, /es/terms) */}
                        <Route path="/:lang" element={<RootRedirect />} />
                        <Route path="/:lang/terms" element={<Terms />} />
                        <Route path="/:lang/dpa" element={<DPA />} />
                        <Route path="/:lang/contact" element={<ContactSales />} />
                        <Route path="/:lang/cookies" element={<Cookies />} />
                        <Route 
                          path="/:lang/login" 
                          element={
                            <PublicRoute>
                              <Login />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/:lang/signup" 
                          element={
                            <PublicRoute>
                              <Signup />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/:lang/verify-email" 
                          element={
                            <PublicRoute>
                              <VerifyEmail />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/:lang/reset-password" 
                          element={
                            <PublicRoute>
                              <ResetPassword />
                            </PublicRoute>
                          } 
                        />
                        
                        {/* Public Routes - simple paths, basename handles /crm prefix automatically */}
                        <Route 
                          path="/login" 
                          element={
                            <PublicRoute>
                              <Login />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/signup" 
                          element={
                            <PublicRoute>
                              <Signup />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/verify-email" 
                          element={
                            <PublicRoute>
                              <VerifyEmail />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/reset-password" 
                          element={
                            <PublicRoute>
                              <ResetPassword />
                            </PublicRoute>
                          } 
                        />
                        <Route 
                          path="/contact" 
                          element={<ContactSales />} 
                        />
                        <Route 
                          path="/terms" 
                          element={<Terms />} 
                        />
                        <Route 
                          path="/cookies" 
                          element={<Cookies />} 
                        />
                        <Route 
                          path="/dpa" 
                          element={<DPA />} 
                        />
                        <Route 
                          path="/portal/accept-invite" 
                          element={<AcceptInvite />} 
                        />
                        
                        {/* Public Forms Route - absolute path, not affected by basename */}
                        <Route 
                          path="/public/forms/:slug" 
                          element={<FormRender />} 
                        />
                        
                        {/* Protected CRM routes - dashboard, clients, etc. */}
                        {/* Note: /* matches everything, so we need to handle root inside CRMSwitch */}
                        <Route 
                          path="/*" 
                          element={<CRMSwitch />}
                        >
                          {/* Dashboard */}
                          <Route index element={<Navigate to="dashboard" replace />} />
                          <Route path="dashboard" element={<Dashboard />} />
                          <Route path="admin" element={<AdminDashboard />} />
                          
                          {/* Test route */}
                          <Route path="test" element={<div className="p-6"><h1>Test Route Works!</h1></div>} />
                          
                          {/* Leads */}
                          <Route path="leads" element={<Leads />} />
                          <Route path="leads/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_CLIENT}>
                              <LeadForm />
                            </PermissionRoute>
                          } />
                          <Route path="leads/:id" element={<LeadDetail />} />
                          <Route path="leads/:id/edit" element={<LeadForm />} />
                          
                          {/* Clients */}
                          <Route path="clients" element={<Clients />} />
                          <Route path="clients/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_CLIENT}>
                              <ClientForm />
                            </PermissionRoute>
                          } />
                          <Route path="clients/:id" element={<ClientDetail />} />
                          <Route path="clients/:id/edit" element={<ClientForm />} />
                          
                          {/* Tasks */}
                          <Route path="tasks" element={<Tasks />} />
                          <Route path="tasks/new" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_TASKS}>
                              <TaskForm />
                            </PermissionRoute>
                          } />
                          <Route path="tasks/:id" element={<TaskDetail />} />
                          <Route path="tasks/:id/edit" element={<TaskForm />} />
                          
                          {/* Task Templates */}
                          <Route path="task-templates" element={<TaskTemplates />} />
                          <Route path="task-templates/new" element={<TaskTemplateForm />} />
                          <Route path="task-templates/:id" element={<TaskTemplateDetail />} />
                          <Route path="task-templates/:id/edit" element={<TaskTemplateForm />} />
                          
                          {/* Settings */}
                          <Route path="settings" element={<Settings />} />
                          <Route path="agency-settings" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_AGENCY_SETTINGS}>
                              <AgencySettings />
                            </PermissionRoute>
                          } />
                          
                          {/* Analytics */}
                          <Route path="analytics" element={<Analytics />} />
                          
                          {/* Additional Routes */}
                          <Route path="accounts" element={<Accounts />} />
                          <Route path="accounts/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_ACCOUNT}>
                              <AccountForm />
                            </PermissionRoute>
                          } />
                          <Route path="accounts/create" element={<AccountForm />} />
                          <Route path="accounts/:id/edit" element={<AccountForm />} />
                          <Route path="accounts/:id" element={<AccountDetailViewWrapper />} />
                          
                          {/* Opportunities */}
                          <Route path="opportunities/new" element={<OpportunityForm />} />
                          <Route path="opportunities/:id/edit" element={<OpportunityForm />} />
                          
                          <Route path="ai-copilot" element={<AICopilot />} />
                          <Route path="engagement-health" element={<EngagementHealth />} />
                          <Route path="collaboration" element={<CollaborationHub />} />
                          {/* Redirect old email-campaigns routes to new campaigns system */}
                          <Route path="email-campaigns" element={<Navigate to="/campaigns" replace />} />
                          <Route path="email-campaigns/new" element={<Navigate to="/campaigns/new" replace />} />
                          <Route path="email-campaigns/:id" element={<EmailCampaignRedirect />} />
                          <Route path="email-campaigns/:id/edit" element={<EmailCampaignRedirect />} />
                          <Route path="email-campaigns/:id/analytics" element={<EmailCampaignRedirect />} />
                          <Route path="campaigns" element={<CampaignsEnhanced />} />
                          <Route path="campaigns/new" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id/edit" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id/analytics" element={<CampaignAnalytics />} />
                          {/* Audiences are now integrated into Campaigns - redirect list page to campaigns */}
                          <Route path="audiences" element={<Navigate to="/campaigns" replace />} />
                          <Route path="audiences/new" element={<AudienceBuilder />} />
                          <Route path="audiences/:id" element={<AudienceBuilder />} />
                          <Route path="audiences/:id/edit" element={<AudienceBuilder />} />
                          <Route path="sending-profiles" element={<SendingProfiles />} />
                          <Route path="sending-profiles/new" element={<SendingProfileBuilder />} />
                          <Route path="sending-profiles/:id" element={<SendingProfileBuilder />} />
                          <Route path="sending-profiles/:id/edit" element={<SendingProfileBuilder />} />
                          <Route path="email-templates" element={<EmailTemplatesEnhanced />} />
                          <Route path="email-templates/new" element={<EmailTemplateBuilder />} />
                          <Route path="email-templates/:id" element={<EmailTemplateBuilder />} />
                          <Route path="email-templates/:id/edit" element={<EmailTemplateBuilder />} />
                          <Route path="calendar" element={<Calendar />} />
                          <Route path="workflows" element={<Workflows />} />
                          <Route path="workflows/create" element={<CreateWorkflow />} />
                          <Route path="workflows/new" element={<CreateWorkflow />} />
                          <Route path="workflows/visual" element={<VisualWorkflowBuilder />} />
                          <Route path="workflows/:id" element={<WorkflowDetail />} />
                          <Route path="workflows/:id/edit" element={<WorkflowEdit />} />
                          <Route path="task-automation" element={<TaskAutomation />} />
                          <Route path="documents" element={<Documents />} />
                          <Route path="locations" element={<Locations />} />
                          <Route path="insights" element={<Insights />} />
                          <Route path="billing" element={<Billing />} />
                          <Route path="subscription" element={<SubscriptionPage />} />
                          <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
                          <Route path="agency/:agencyId/billing" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_AGENCY_SETTINGS}>
                              <AgencyBilling />
                            </PermissionRoute>
                          } />
                          <Route path="client-portal" element={<ClientPortal />} />
                          <Route path="multi-language" element={<MultiLanguage />} />
                          {/* Forms */}
                          <Route path="forms" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <FormsHome />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/builder" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <FormBuilder />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/responses" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormResponses />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/responses/:submissionId" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormResponseDetail />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/analytics" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormAnalytics />
                            </PermissionRoute>
                          } />
                          <Route path="forms/templates" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <TemplatesGallery />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/preview" element={<div className="p-6 text-sm text-gray-600">Publish the form, then open the public link to preview. Public route: <code>/public/forms/:slug</code>.</div>} />
                          
                          {/* New Enhanced Pages */}
                          <Route path="consultant-mode" element={<ConsultantMode />} />
                          <Route path="relocation-journey" element={<RelocationJourney />} />
                          <Route path="relocation-journey/:clientId" element={<RelocationJourney />} />
                          <Route path="relocation-workflows" element={<RelocationWorkflowTemplates />} />
                          <Route path="relocation-workspace" element={<RelocationWorkspaceSelector />} />
                          <Route path="relocation-workspace/:clientId" element={<RelocationWorkspace />} />
                        </Route>
                        
                        {/* Language-prefixed CRM routes (e.g., /fr/crm, /es/crm) */}
                        <Route 
                          path="/:lang/crm/*" 
                          element={<CRMSwitch />}
                        >
                          {/* Dashboard */}
                          <Route index element={<LanguageAwareNavigate to="/dashboard" />} />
                          <Route path="dashboard" element={<Dashboard />} />
                          
                          {/* Test route */}
                          <Route path="test" element={<div className="p-6"><h1>Test Route Works!</h1></div>} />
                          
                          {/* Leads */}
                          <Route path="leads" element={<Leads />} />
                          <Route path="leads/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_CLIENT}>
                              <LeadForm />
                            </PermissionRoute>
                          } />
                          <Route path="leads/:id" element={<LeadDetail />} />
                          <Route path="leads/:id/edit" element={<LeadForm />} />
                          
                          {/* Clients */}
                          <Route path="clients" element={<Clients />} />
                          <Route path="clients/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_CLIENT}>
                              <ClientForm />
                            </PermissionRoute>
                          } />
                          <Route path="clients/:id" element={<ClientDetail />} />
                          <Route path="clients/:id/edit" element={<ClientForm />} />
                          
                          {/* Tasks */}
                          <Route path="tasks" element={<Tasks />} />
                          <Route path="tasks/new" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_TASKS}>
                              <TaskForm />
                            </PermissionRoute>
                          } />
                          <Route path="tasks/:id" element={<TaskDetail />} />
                          <Route path="tasks/:id/edit" element={<TaskForm />} />
                          
                          {/* Task Templates */}
                          <Route path="task-templates" element={<TaskTemplates />} />
                          <Route path="task-templates/new" element={<TaskTemplateForm />} />
                          <Route path="task-templates/:id" element={<TaskTemplateDetail />} />
                          <Route path="task-templates/:id/edit" element={<TaskTemplateForm />} />
                          
                          {/* Settings */}
                          <Route path="settings" element={<Settings />} />
                          <Route path="agency-settings" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_AGENCY_SETTINGS}>
                              <AgencySettings />
                            </PermissionRoute>
                          } />
                          
                          {/* Analytics */}
                          <Route path="analytics" element={<Analytics />} />
                          
                          {/* Additional Routes */}
                          <Route path="accounts" element={<Accounts />} />
                          <Route path="accounts/new" element={
                            <PermissionRoute permission={PERMISSIONS.CREATE_ACCOUNT}>
                              <AccountForm />
                            </PermissionRoute>
                          } />
                          <Route path="accounts/create" element={<AccountForm />} />
                          <Route path="accounts/:id/edit" element={<AccountForm />} />
                          <Route path="accounts/:id" element={<AccountDetailViewWrapper />} />
                          
                          {/* Opportunities */}
                          <Route path="opportunities/new" element={<OpportunityForm />} />
                          <Route path="opportunities/:id/edit" element={<OpportunityForm />} />
                          
                          <Route path="ai-copilot" element={<AICopilot />} />
                          <Route path="engagement-health" element={<EngagementHealth />} />
                          <Route path="collaboration" element={<CollaborationHub />} />
                          {/* Redirect old email-campaigns routes to new campaigns system */}
                          <Route path="email-campaigns" element={<Navigate to="/campaigns" replace />} />
                          <Route path="email-campaigns/new" element={<Navigate to="/campaigns/new" replace />} />
                          <Route path="email-campaigns/:id" element={<EmailCampaignRedirect />} />
                          <Route path="email-campaigns/:id/edit" element={<EmailCampaignRedirect />} />
                          <Route path="email-campaigns/:id/analytics" element={<EmailCampaignRedirect />} />
                          <Route path="campaigns" element={<CampaignsEnhanced />} />
                          <Route path="campaigns/new" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id/edit" element={<CampaignBuilder />} />
                          <Route path="campaigns/:id/analytics" element={<CampaignAnalytics />} />
                          {/* Audiences are now integrated into Campaigns - redirect list page to campaigns */}
                          <Route path="audiences" element={<Navigate to="/campaigns" replace />} />
                          <Route path="audiences/new" element={<AudienceBuilder />} />
                          <Route path="audiences/:id" element={<AudienceBuilder />} />
                          <Route path="audiences/:id/edit" element={<AudienceBuilder />} />
                          <Route path="sending-profiles" element={<SendingProfiles />} />
                          <Route path="sending-profiles/new" element={<SendingProfileBuilder />} />
                          <Route path="sending-profiles/:id" element={<SendingProfileBuilder />} />
                          <Route path="sending-profiles/:id/edit" element={<SendingProfileBuilder />} />
                          <Route path="email-templates" element={<EmailTemplatesEnhanced />} />
                          <Route path="email-templates/new" element={<EmailTemplateBuilder />} />
                          <Route path="email-templates/:id" element={<EmailTemplateBuilder />} />
                          <Route path="email-templates/:id/edit" element={<EmailTemplateBuilder />} />
                          <Route path="calendar" element={<Calendar />} />
                          <Route path="workflows" element={<Workflows />} />
                          <Route path="workflows/create" element={<CreateWorkflow />} />
                          <Route path="workflows/new" element={<CreateWorkflow />} />
                          <Route path="workflows/visual" element={<VisualWorkflowBuilder />} />
                          <Route path="workflows/:id" element={<WorkflowDetail />} />
                          <Route path="workflows/:id/edit" element={<WorkflowEdit />} />
                          <Route path="task-automation" element={<TaskAutomation />} />
                          <Route path="documents" element={<Documents />} />
                          <Route path="locations" element={<Locations />} />
                          <Route path="insights" element={<Insights />} />
                          <Route path="billing" element={<Billing />} />
                          <Route path="subscription" element={<SubscriptionPage />} />
                          <Route path="subscription/success" element={<SubscriptionSuccessPage />} />
                          <Route path="agency/:agencyId/billing" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_AGENCY_SETTINGS}>
                              <AgencyBilling />
                            </PermissionRoute>
                          } />
                          <Route path="client-portal" element={<ClientPortal />} />
                          <Route path="multi-language" element={<MultiLanguage />} />
                          {/* Forms */}
                          <Route path="forms" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <FormsHome />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/builder" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <FormBuilder />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/responses" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormResponses />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/responses/:submissionId" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormResponseDetail />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/analytics" element={
                            <PermissionRoute permission={PERMISSIONS.VIEW_FORM_SUBMISSIONS}>
                              <FormAnalytics />
                            </PermissionRoute>
                          } />
                          <Route path="forms/templates" element={
                            <PermissionRoute permission={PERMISSIONS.MANAGE_FORMS}>
                              <TemplatesGallery />
                            </PermissionRoute>
                          } />
                          <Route path="forms/:id/preview" element={<div className="p-6 text-sm text-gray-600">Publish the form, then open the public link to preview. Public route: <code>/public/forms/:slug</code>.</div>} />
                          
                          {/* New Enhanced Pages */}
                          <Route path="consultant-mode" element={<ConsultantMode />} />
                          <Route path="relocation-journey" element={<RelocationJourney />} />
                          <Route path="relocation-journey/:clientId" element={<RelocationJourney />} />
                          <Route path="relocation-workflows" element={<RelocationWorkflowTemplates />} />
                          <Route path="relocation-workspace" element={<RelocationWorkspaceSelector />} />
                          <Route path="relocation-workspace/:clientId" element={<RelocationWorkspace />} />
                        </Route>
                        
                        {/* 404 Route */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </div>
                    </DialogProvider>
                  </TourProvider>
                </NotificationProvider>
              </CommandPaletteProvider>
          </SystemConfigProvider>
            </BrandingProvider>
          </FeatureRegistryProvider>
          </CRMProvider>
        </LanguageProvider>
      </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;